import { CommonModule } from '@angular/common';
import { inject, ApplicationRef,
  ChangeDetectorRef,
  Component,
  computed,
  Inject,
  Injector,
  TemplateRef,
  ChangeDetectionStrategy
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { title } from '@softline/application';
import { base64Encode, Store } from '@softline/core';
import { translator } from '@softline/ui-core';
import { BehaviorSubject, map } from 'rxjs';
import {
  EmpfaengerComponentRegistration,
  SOFTLINE_CONFIG_SEND_NOTIFICATION_COMPONENT,
  SOFTLINE_STEPS_SEND_NOTIFICATION,
} from '../../send-notification.shared';
import {
  SendNotificationStore,
  SOFTLINE_FEATURE_SEND_NOTIFICATION,
} from '../../store/send-notification.store';

@Component({
  selector: 'soft-empfaenger-auswahl',
  imports: [CommonModule],
  templateUrl: './empfaenger-auswahl.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./empfaenger-auswahl.component.css'],
})
export class EmpfaengerAuswahlComponent {
  private readonly t = translator();
  readonly steps = SOFTLINE_STEPS_SEND_NOTIFICATION;

  private readonly fragment$ = this.route.fragment;

  readonly headerTemplate$ = new BehaviorSubject<TemplateRef<any> | null>(null);

  readonly component$ = this.fragment$.pipe(
    map((fragment) => fragment ?? 'DEFAULT'),
    map((fragment) => this.empfaengerComponents.find((o) => o?.name === fragment)),
    map((componentRegistration) =>
      componentRegistration ? componentRegistration.component : EmpfaengerAuswahlComponent
    )
  );

  readonly data = this.store.signal(
    SOFTLINE_FEATURE_SEND_NOTIFICATION,
    SendNotificationStore.getters.data
  );

  readonly empfaenger = computed(() => this.data()?.empfaenger ?? []);

  readonly empfaengerComponents: EmpfaengerComponentRegistration[];

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly store: Store,
    readonly injector: Injector,
    private cdRef: ChangeDetectorRef,
    @Inject(SOFTLINE_CONFIG_SEND_NOTIFICATION_COMPONENT)
    injectedEmpfaengerComponents: EmpfaengerComponentRegistration[]
  ) {
    // route-level multi providers (shell-scoped module globals) SHADOW root
    // registrations — union both injector levels (dedupe by reference)
    const rootComponents =
      (inject(ApplicationRef).injector.get(SOFTLINE_CONFIG_SEND_NOTIFICATION_COMPONENT, null, {
        optional: true,
      }) as EmpfaengerComponentRegistration[] | null) ?? [];
    this.empfaengerComponents = [
      ...new Set([...rootComponents, ...(injectedEmpfaengerComponents ?? [])]),
    ];
    title(this.t('#SEND_NOTIFICATION.TITLE'));
  }

  async nachrichtVerfassen(): Promise<void> {
    const from = this.route.snapshot.queryParamMap.get('from');

    await this.router.navigate(
      ['/send-notification/nachricht-verfassen'],
      from
        ? {
            queryParams: {
              from: base64Encode(
                JSON.stringify({
                  url: ['/send-notification'],
                  extras: {
                    queryParams: { from },
                    fragment: this.route.snapshot.fragment,
                  },
                })
              ),
            },
          }
        : {}
    );
  }

  attachHeader(template: TemplateRef<any>): void {
    this.headerTemplate$.next(template);
    this.cdRef.detectChanges();
  }
}
